// src/pages/Users/LastGallerie.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllGalleries } from "../../services/gallerieServices";
import Button from "../../components/Button/Button";
import { useUser } from "../Home/UserContext";
import SectionTitle from "../../components/ui/SectionTitle";
import {
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiX,
  FiImage,
} from "react-icons/fi";

// ✅ rend les /uploads/... absolues vers l’API, laisse passer les https://
import { toAbsolutePhotoUrl as toAbsPhoto } from "../../services/albumPhotoServices";

const FALLBACK = "/img/no-image.jpg";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

export default function LastGallerie() {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);

  // modal image + navigation
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    (async () => {
      try {
        const all = await getAllGalleries();
        const active = (all || []).filter((g) => g.is_active);
        setGalleries(active.slice(0, 3)); // Home: 3 derniers albums
      } catch (e) {
        console.error("Failed to fetch galleries", e);
        setGalleries([]);
      }
    })();
  }, []);

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  // Helpers: tri des photos + cover
  const orderPhotos = useCallback((photos = []) => {
    const arr = Array.isArray(photos) ? [...photos] : [];
    arr.sort((a, b) => {
      const pa = Number.isFinite(a?.position)
        ? a.position
        : Number.MAX_SAFE_INTEGER;
      const pb = Number.isFinite(b?.position)
        ? b.position
        : Number.MAX_SAFE_INTEGER;
      return pa - pb || (a?.id ?? 0) - (b?.id ?? 0);
    });
    return arr;
  }, []);

  const getCoverPhoto = useCallback(
    (gallery) => {
      const ordered = orderPhotos(gallery?.photos || []);
      return ordered.find((p) => p?.is_cover === true) || ordered[0] || null;
    },
    [orderPhotos],
  );

  const openAlbum = (gallery) => setSelectedGallery(gallery);
  const closeAlbum = () => {
    setSelectedGallery(null);
    setLightboxOpen(false);
  };

  const openLightbox = (idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);

  const handleReadMore = (galleryId) => {
    navigate(`/galerie-photos/album/${galleryId}`);
  };

  // Photos de l’album sélectionné (triées)
  const selectedPhotos = useMemo(() => {
    return orderPhotos(selectedGallery?.photos || []);
  }, [selectedGallery, orderPhotos]);

  const totalPhotos = selectedPhotos.length;

  // navigation lightbox
  const prevPhoto = useCallback(() => {
    if (!totalPhotos) return;
    setLightboxIndex((i) => (i - 1 + totalPhotos) % totalPhotos);
  }, [totalPhotos]);

  const nextPhoto = useCallback(() => {
    if (!totalPhotos) return;
    setLightboxIndex((i) => (i + 1) % totalPhotos);
  }, [totalPhotos]);

  // Keyboard shortcuts in lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prevPhoto();
      if (e.key === "ArrowRight") nextPhoto();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, prevPhoto, nextPhoto]);

  return (
    <section
      className={cn(
        "py-12 sm:py-16",
        "bg-lightBackground dark:bg-darkBackground",
        "text-black dark:text-white",
      )}
      id="albums"
    >
      {/* Animations locales */}
      <style>{`
        @keyframes gal-in { from { opacity:.0; transform: translateY(10px);} to {opacity:1; transform: translateY(0);} }
        @media (prefers-reduced-motion: reduce) {
          .gal-anim { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-4">
        <SectionTitle
          title="Derniers albums photos"
          subtitle="Revivez les moments forts du club"
        />

        {/* Cards */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {galleries.map((gallery) => {
            const photos = orderPhotos(gallery?.photos || []);
            const cover = getCoverPhoto(gallery);
            const coverSrc = cover?.photo_url
              ? toAbsPhoto(cover.photo_url)
              : FALLBACK;

            // preview mosaic: 1 grande + 3 petites
            const p1 = coverSrc || FALLBACK;
            const p2 = photos[1]?.photo_url
              ? toAbsPhoto(photos[1].photo_url)
              : null;
            const p3 = photos[2]?.photo_url
              ? toAbsPhoto(photos[2].photo_url)
              : null;
            const p4 = photos[3]?.photo_url
              ? toAbsPhoto(photos[3].photo_url)
              : null;

            return (
              <article
                key={gallery.id}
                className={cn(
                  "gal-anim overflow-hidden rounded-3xl",
                  "border border-slate-200/70 dark:border-slate-800",
                  "bg-white/80 dark:bg-slate-900/60 backdrop-blur",
                  "shadow-sm hover:shadow-lg hover:-translate-y-[2px]",
                  "transition duration-300 motion-reduce:transition-none",
                )}
                style={{ animation: "gal-in .35s ease-out both" }}
              >
                {/* Cover only (click to open album modal) */}
                <button
                  type="button"
                  onClick={() => openAlbum(gallery)}
                  className="block w-full text-left"
                  aria-label={`Ouvrir l'album ${gallery.titre}`}
                  title="Prévisualiser l'album"
                >
                  <div className="relative overflow-hidden rounded-3xl">
                    <img
                      src={coverSrc || FALLBACK}
                      alt={gallery.titre}
                      className={cn(
                        "h-64 w-full object-cover",
                        "transition duration-500 group-hover:scale-[1.02] motion-reduce:transition-none",
                      )}
                      onError={onImgError}
                      loading="lazy"
                      decoding="async"
                    />

                    {/* Overlay léger (lisibilité si tu veux du texte un jour, sinon juste esthétique) */}
                    <div
                      aria-hidden
                      className="absolute inset-0 bg-gradient-to-t from-black/10 via-black/0 to-black/0"
                    />

                    {/* Badge nb photos */}
                    <div
                      className={cn(
                        "absolute left-4 top-4 inline-flex items-center gap-2",
                        "rounded-full px-3 py-1 text-xs font-bold",
                        "bg-white/85 dark:bg-slate-950/60 backdrop-blur",
                        "border border-slate-200/70 dark:border-slate-800",
                        "text-slate-800 dark:text-slate-100",
                      )}
                    >
                      <FiImage />
                      {photos.length || 0}
                    </div>

                    {/* Petit bouton visuel “zoom” */}
                    <div
                      aria-hidden
                      className={cn(
                        "absolute right-4 top-4 h-9 w-9 rounded-full grid place-items-center",
                        "bg-white/85 dark:bg-slate-950/60 backdrop-blur",
                        "border border-slate-200/70 dark:border-slate-800",
                        "text-slate-800 dark:text-slate-100",
                      )}
                    >
                      <FiMaximize2 />
                    </div>
                  </div>
                </button>

                {/* Text content */}
                <div className="px-5 pb-5">
                  <h3 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50">
                    {gallery.titre}
                  </h3>

                  {gallery.description ? (
                    <div
                      className={cn(
                        "mt-2 text-sm text-slate-600 dark:text-slate-300",
                        "line-clamp-3",
                        // rendre les <strong> visibles en dark si description HTML
                        "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                        "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold",
                      )}
                      dangerouslySetInnerHTML={{ __html: gallery.description }}
                    />
                  ) : (
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 italic">
                      Album photo du club
                    </p>
                  )}

                  <div className="mt-5 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => openAlbum(gallery)}
                      className={cn(
                        "inline-flex items-center gap-2 text-sm font-semibold",
                        "text-primary hover:opacity-90 transition",
                      )}
                      aria-label="Prévisualiser l'album"
                    >
                      Prévisualiser
                      <span aria-hidden>→</span>
                    </button>

                    {user && (
                      <Button
                        text="Voir l'album"
                        onClick={() => handleReadMore(gallery.id)}
                        className="px-5"
                      />
                    )}
                  </div>
                </div>

                {/* accent bas */}
                <div
                  aria-hidden
                  className="h-1.5 w-full opacity-70"
                  style={{
                    background:
                      "linear-gradient(to right, transparent, var(--tw-color-primary, #2563eb), transparent)",
                  }}
                />
              </article>
            );
          })}
        </div>

        {/* MODALE ALBUM (grille moderne + scroll interne) */}
        {selectedGallery && (
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
            onClick={closeAlbum}
            role="dialog"
            aria-modal="true"
          >
            <div
              className={cn(
                "relative w-full max-w-6xl overflow-hidden rounded-3xl",
                "bg-white/90 dark:bg-slate-950/80 backdrop-blur",
                "border border-slate-200/70 dark:border-slate-800",
                "shadow-2xl",
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={closeAlbum}
                className={cn(
                  "absolute right-4 top-4 z-10",
                  "h-10 w-10 rounded-full grid place-items-center",
                  "bg-white/85 dark:bg-slate-950/60 backdrop-blur",
                  "border border-slate-200/70 dark:border-slate-800",
                  "text-slate-800 dark:text-slate-100",
                  "shadow hover:shadow-md transition",
                )}
                aria-label="Fermer l'album"
              >
                <FiX className="text-xl" />
              </button>

              {/* Grid photos (scroll interne) */}
              <div className="mt-6 max-h-[70vh] overflow-y-auto pr-1">
                {selectedPhotos.length === 0 ? (
                  <div className="py-12 text-center text-sm text-slate-600 dark:text-slate-300 italic">
                    Aucune photo dans cet album.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {selectedPhotos.map((photo, idx) => {
                      const src = photo?.photo_url
                        ? toAbsPhoto(photo.photo_url)
                        : FALLBACK;

                      return (
                        <button
                          key={photo.id ?? idx}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation(); // ✅ évite de fermer la modale album
                            openLightbox(idx);
                          }}
                          className={cn(
                            "group relative overflow-hidden rounded-2xl",
                            "border border-slate-200/60 dark:border-slate-800/60",
                            "bg-slate-100 dark:bg-slate-900/40",
                            "hover:shadow-md transition",
                          )}
                          aria-label="Ouvrir la photo"
                          title="Ouvrir"
                        >
                          <img
                            src={src || FALLBACK}
                            alt={photo.titre || selectedGallery.titre}
                            className={cn(
                              "h-36 sm:h-40 w-full object-cover",
                              "transition duration-500 group-hover:scale-[1.03]",
                            )}
                            onError={onImgError}
                            loading="lazy"
                          />
                          <span
                            aria-hidden
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition bg-gradient-to-t from-black/25 via-black/0 to-black/0"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {user && (
                <div className="mt-6 sm:hidden flex justify-center">
                  <Button
                    text="Voir l’album"
                    onClick={() => handleReadMore(selectedGallery.id)}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* LIGHTBOX IMAGE (navigation + ESC + compteur) */}
        {selectedGallery && lightboxOpen && totalPhotos > 0 && (
          <div
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3"
            onClick={closeLightbox}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="relative w-full max-w-6xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={closeLightbox}
                className="absolute -top-3 -right-3 h-11 w-11 rounded-full bg-white/90 text-slate-900 text-2xl grid place-items-center shadow-lg hover:text-primary transition"
                aria-label="Fermer"
              >
                &times;
              </button>

              {/* Nav */}
              {totalPhotos > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevPhoto}
                    className={cn(
                      "absolute left-2 top-1/2 -translate-y-1/2",
                      "h-11 w-11 rounded-full grid place-items-center",
                      "bg-white/85 text-slate-900 shadow hover:shadow-md transition",
                    )}
                    aria-label="Photo précédente"
                  >
                    <FiChevronLeft className="text-xl" />
                  </button>

                  <button
                    type="button"
                    onClick={nextPhoto}
                    className={cn(
                      "absolute right-2 top-1/2 -translate-y-1/2",
                      "h-11 w-11 rounded-full grid place-items-center",
                      "bg-white/85 text-slate-900 shadow hover:shadow-md transition",
                    )}
                    aria-label="Photo suivante"
                  >
                    <FiChevronRight className="text-xl" />
                  </button>
                </>
              )}

              {/* Image */}
              <img
                src={
                  toAbsPhoto(selectedPhotos[lightboxIndex]?.photo_url) ||
                  FALLBACK
                }
                alt="Agrandissement"
                className="w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl bg-black/20"
                onError={onImgError}
              />

              {/* Compteur */}
              <div className="mt-3 text-center text-sm text-white/85">
                {lightboxIndex + 1} / {totalPhotos}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
